import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  useGetHoldQuery,
  useCreateHoldMutation,
  useUpdateHoldMutation,
  useDeleteHoldMutation,
  useUnholdHoldMutation,
  useLazyGetCustomersQuery,
} from '../../../store/api/salesApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { CustomerFormContent } from './CustomerForm';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../store/slices/authSlice';
import { usePermission } from '../../../hooks/usePermission';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const DEFAULT_VAT = 5;

function HoldForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const { hasPermission, isAdmin } = usePermission();
  const { data: company } = useGetCurrentCompanyQuery();
  const isWholesaleEnabled = company?.showWholesalePrice !== false;
  const canWholesale = isWholesaleEnabled && (isAdmin || hasPermission('sales.orders.wholesale_sale'));

  const currentUser = useSelector(selectUser);

  const { data: hold, isLoading: loadingHold } = useGetHoldQuery(id, { skip: isNew });
  const [createHold] = useCreateHoldMutation();
  const [updateHold] = useUpdateHoldMutation();
  const [deleteHold] = useDeleteHoldMutation();
  const [unholdHold] = useUnholdHoldMutation();
  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();

  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const isTaxEnabled = company?.taxLabel !== 'None' && Number(company?.vatRate ?? 0) > 0;
  const defaultVat = company?.taxLabel === 'None' ? 0 : Number(company?.vatRate ?? 0);
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';
  const creditDaysOptions = (company?.creditDaysOptions || '7,14,30')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);

  const [formData, setFormData] = useState({
    customerId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    orderDate: new Date().toISOString().split('T')[0],
    salesType: 'credit',
    creditDays: '',
    salesPersonId: currentUser?.id || '',
    locationId: '',
    notes: '',
    roundOff: 0,
  });
  const [items, setItems] = useState([]);
  const [pricingMode, setPricingMode] = useState('retail'); // 'retail' | 'wholesale'

  const handlePricingModeChange = (newMode) => {
    if (newMode === 'wholesale' && !canWholesale) return;
    setPricingMode(newMode);
    if (items.length > 0) {
      setItems((prev) =>
        prev.map((item) => {
          let basePrice = 0;
          if (newMode === 'wholesale') {
            basePrice = (item.wholesalePrice != null && Number(item.wholesalePrice) > 0)
              ? Number(item.wholesalePrice)
              : Number(item.sellingPrice != null ? item.sellingPrice : item.unitPrice || 0);
          } else {
            basePrice = Number(item.sellingPrice != null ? item.sellingPrice : item.unitPrice || 0);
          }
          const itemVat = item.vatPercent != null ? (Number.isFinite(Number(item.vatPercent)) ? Number(item.vatPercent) : 0) : defaultVat;
          const calculatedUnitPrice = (vatIncluded && itemVat > 0)
            ? Number((basePrice / (1 + itemVat / 100)).toFixed(company?.currencyDecimals ?? 2))
            : basePrice;
          return {
            ...item,
            unitPrice: calculatedUnitPrice,
            inclusivePriceInput: undefined,
            pricingType: newMode,
          };
        })
      );
    }
  };

  useEffect(() => {
    if (!canWholesale && pricingMode === 'wholesale') {
      handlePricingModeChange('retail');
    }
  }, [canWholesale, pricingMode]);

  const [productSearch, setProductSearch] = useState('');
  const [quickScanPrice, setQuickScanPrice] = useState('');
  const [lockQuickScanPrice, setLockQuickScanPrice] = useState(false);
  const quickPriceInputRef = useRef(null);
  const [payments, setPayments] = useState([
    { method: 'cash', paymentAccountId: '', referenceNo: '', amount: '' },
  ]);
  const [autoPaymentAmount, setAutoPaymentAmount] = useState(true);
  const [billDiscount, setBillDiscount] = useState(0);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (hold && !isNew) {
      setFormData({
        customerId: hold.customerId || '',
        invoiceDate: hold.invoiceDate ? hold.invoiceDate.substring(0, 10) : formData.invoiceDate,
        orderDate: hold.orderDate ? hold.orderDate.substring(0, 10) : formData.orderDate,
        salesType: hold.salesType || 'credit',
        creditDays: hold.creditDays != null ? String(hold.creditDays) : '',
        salesPersonId: hold.salesPersonId || '',
        locationId: hold.locationId || '',
        notes: hold.notes || '',
        roundOff: hold.roundOff || 0,
      });
      const mapped = (hold.items || []).map((it) => ({
        productId: it.productId,
        productName: it.product?.name,
        productSku: it.product?.sku,
        productBrand: it.product?.brand,
        productBarcode: it.product?.barcode,
        quantity: it.quantity,
        unit: it.unit || 'pcs',
        unitPrice: (vatIncluded && (Number(it.vatPercent) || defaultVat) > 0)
          ? Number((it.unitPrice / (1 + (Number(it.vatPercent) || defaultVat) / 100)).toFixed(company?.currencyDecimals ?? 2))
          : it.unitPrice,
        discount: Number(it.discount) || 0,
        vatPercent: Number(it.vatPercent) || defaultVat,
        batchNo: it.batchNo || '',
        expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
        productType: it.product?.type || 'product',
        serviceData: it.serviceData ? (typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData) : null,
      }));
      setItems(mapped);
      setBillDiscount(Number(hold.billDiscount) || 0);
      if (Array.isArray(hold.payments) && hold.payments.length > 0) {
        setPayments(
          hold.payments.map((p) => ({
            method: p.method || 'cash',
            paymentAccountId: p.paymentAccountId || '',
            referenceNo: p.referenceNo || '',
            amount: String(p.amount ?? ''),
          }))
        );
      }
    }
  }, [hold, isNew]);

  useEffect(() => {
    if (customerSearchData?.data) {
      setCustomerSearchOptions(customerSearchData.data);
    } else if (Array.isArray(customerSearchData)) {
      setCustomerSearchOptions(customerSearchData);
    }
  }, [customerSearchData]);

  useEffect(() => {
    if (hold?.customer && !customerSearchOptions.find(c => c.id === hold.customer.id)) {
      setCustomerSearchOptions(prev => [hold.customer, ...prev]);
    }
  }, [hold, customerSearchOptions]);

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  useEffect(() => {
    if (isNew && locations?.length && !formData.locationId) {
      const mainWarehouse = locations.find((l) => l.name.toLowerCase().includes('main warehouse'));
      if (mainWarehouse) {
        setFormData((prev) => ({ ...prev, locationId: mainWarehouse.id }));
      } else if (locations.length > 0) {
        setFormData((prev) => ({ ...prev, locationId: locations[0].id }));
      }
    }
  }, [locations, isNew]);


  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const computeLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      vatAmount = lineSubtotal * (vatPct / 100);
    }
    const lineTotal = lineSubtotal + vatAmount;
    return {
      vatAmount: Math.round((vatAmount) * Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) / Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      lineTotal: Math.round((lineTotal) * Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) / Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
    };
  };

  const orderSubtotal = items.reduce(
    (sum, item) => sum + toNum(item.quantity) * toNum(item.unitPrice),
    0
  );
  const lineDiscountTotal = items.reduce((sum, item) => sum + toNum(item.discount), 0);
  const orderDiscountTotal = lineDiscountTotal + toNum(billDiscount);
  const orderVatTotal = items.reduce((sum, item) => sum + computeLine(item).vatAmount, 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = netOrder + orderVatTotal;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;
  const paidTotal = payments.reduce(
    (sum, p) => sum + (Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0),
    0
  );
  const remaining = totalAmount - paidTotal;
  const change = paidTotal - totalAmount;

  // Auto-sync first payment for cash sales if not manually edited
  useEffect(() => {
    if (
      formData.salesType === 'cash' &&
      payments.length === 1 &&
      autoPaymentAmount
    ) {
      const target = totalAmount.toFixed(company?.currencyDecimals ?? 2);
      if (payments[0].amount !== target) {
        const next = [...payments];
        next[0] = { ...next[0], amount: target };
        setPayments(next);
      }
    }
  }, [totalAmount, formData.salesType, payments, autoPaymentAmount, company]);

  const handleAddProductRow = (product) => {
    if (!product) return;
    const behavior = company?.barcodeAfterScanBehaviorSales || company?.barcodeAfterScanBehavior || 'search';
    
    const hasCustomPrice = quickScanPrice !== '' && !isNaN(Number(quickScanPrice)) && Number(quickScanPrice) >= 0;
    const customPriceNum = hasCustomPrice ? Number(quickScanPrice) : null;

    const rawBasePrice =
      customPriceNum !== null
        ? customPriceNum
        : (pricingMode === 'wholesale' && product.wholesalePrice != null && Number(product.wholesalePrice) > 0
          ? Number(product.wholesalePrice)
          : (product.sellingPrice ?? 0));
    const computedUnitPrice =
      vatIncluded && defaultVat > 0
        ? Number((rawBasePrice / (1 + defaultVat / 100)).toFixed(company?.currencyDecimals ?? 2))
        : rawBasePrice;

    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1) {
        if (behavior === 'auto_increment') {
          const updated = [...prev];
          const curQty = Number(updated[existingIndex].quantity || 0) || 0;
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: String(Math.max(0.01, curQty + 1)),
            ...(hasCustomPrice && {
              unitPrice: computedUnitPrice,
              sellingPrice: customPriceNum,
              pricingType: 'custom',
            }),
          };
          setTimeout(() => {
            const el = document.getElementById('product-search-input');
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
          return updated;
        }

        if (behavior === 'quantity') {
          setTimeout(() => {
            const el = document.getElementById(`qty-input-${existingIndex}`);
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
        } else if (behavior === 'price') {
          setTimeout(() => {
            const el = document.getElementById(`price-input-${existingIndex}`);
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
        } else {
          setTimeout(() => {
            const el = document.getElementById('product-search-input');
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
        }
        return prev;
      }

      const newIndex = prev.length;
      if (behavior === 'quantity') {
        setTimeout(() => {
          const el = document.getElementById(`qty-input-${newIndex}`);
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      } else if (behavior === 'price') {
        setTimeout(() => {
          const el = document.getElementById(`price-input-${newIndex}`);
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      } else {
        setTimeout(() => {
          const el = document.getElementById('product-search-input');
          if (el) {
            el.focus();
            el.select();
          }
        }, 100);
      }

      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          productBarcode: product.barcode,
          sellingPrice: customPriceNum !== null ? customPriceNum : (product.sellingPrice ?? 0),
          wholesalePrice: product.wholesalePrice ?? 0,
          pricingType:
            customPriceNum !== null
              ? 'custom'
              : (pricingMode === 'wholesale' && product.wholesalePrice != null && Number(product.wholesalePrice) > 0
                ? 'wholesale'
                : 'retail'),
          quantity: 1,
          unit: 'pcs',
          unitPrice: computedUnitPrice,
          discount: 0,
          vatPercent: defaultVat,
          batchNo: '',
          expiryDate: '',
          productType: product.type || 'product',
          serviceData: product.type === 'service' ? { imei: '', deviceModel: '', complains: '' } : null,
        },
      ];
    });

    if (hasCustomPrice && !lockQuickScanPrice) {
      setQuickScanPrice('');
    }
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const getPayload = () => ({
    ...formData,
    invoiceDate: formData.invoiceDate,
    orderDate: formData.orderDate || formData.invoiceDate,
    salesType: formData.salesType,
    creditDays:
      formData.salesType === 'credit' && formData.creditDays
        ? parseInt(formData.creditDays, 10)
        : undefined,
    salesPersonId: formData.salesPersonId || undefined,
    locationId: formData.locationId || undefined,
    billDiscount: Number(billDiscount) || 0,
    roundOff: Number(formData.roundOff) || 0,
    payments: payments.map((p) => ({
      method: p.method,
      paymentAccountId: p.paymentAccountId || undefined,
      referenceNo: p.referenceNo || undefined,
      amount: Number(p.amount || 0),
    })),
    items: items.map((item) => {
      const { vatAmount } = computeLine(item);
      return {
        productId: item.productId,
        quantity: parseFloat(item.quantity),
        unit: item.unit || undefined,
        unitPrice: vatIncluded && (toNum(item.vatPercent) || defaultVat) > 0 
          ? parseFloat(item.unitPrice) * (1 + (toNum(item.vatPercent) || defaultVat) / 100)
          : parseFloat(item.unitPrice),
        discount: toNum(item.discount),
        vatPercent: toNum(item.vatPercent) || defaultVat,
        vatAmount,
        batchNo: item.batchNo || undefined,
        expiryDate: item.expiryDate || undefined,
        serviceData: item.serviceData ? JSON.stringify(item.serviceData) : null,
      };
    }),
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }
    const payload = getPayload();
    try {
      if (isNew) {
        const created = await createHold(payload).unwrap();
        navigate(created?.id ? `/sales/holds/${created.id}` : '/sales/holds');
      } else {
        await updateHold({ id, ...payload }).unwrap();
        navigate('/sales/holds');
      }
    } catch (err) {
      alert(err?.data?.message || 'Failed to save hold');
    }
  };

  const handleUnhold = async () => {
    if (!formData.customerId) {
      alert('Add a customer before unholding');
      return;
    }
    if (formData.salesType === 'cash' && paidTotal + 0.0001 < totalAmount) {
      alert('Cash sales must be fully paid before unholding');
      return;
    }
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }
    try {
      const payload = getPayload();
      await updateHold({ id, ...payload }).unwrap();
      const result = await unholdHold(id).unwrap();
      const orderId = result?.createdOrder?.id;
      if (orderId) navigate(`/sales/orders/${orderId}`);
      else navigate('/sales');
    } catch (err) {
      alert(err?.data?.message || 'Unhold failed');
    }
  };

  const handleDelete = async () => {
    setDeleteConfirmOpen(false);
    try {
      await deleteHold(id).unwrap();
      navigate('/sales/holds');
    } catch (err) {
      alert(err?.data?.message || 'Delete failed');
    }
  };

  if (!isNew && loadingHold) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isNew ? 'New Hold' : `Edit Hold: ${hold?.holdNumber || id}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/sales/holds')}>
            Back to Holds
          </Button>
          {!isNew && (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={!formData.customerId || (formData.salesType === 'cash' && paidTotal + 0.0001 < totalAmount)}
                onClick={handleUnhold}
              >
                Unhold (convert to order)
              </Button>
              <Button variant="outlined" color="error" onClick={() => setDeleteConfirmOpen(true)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSave}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  fullWidth
                  options={customerSearchOptions}
                  loading={searchingCustomers}
                  getOptionLabel={(opt) => opt?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  getOptionDisabled={(option) => !canWholesale && option?.customerType === 'wholesale'}
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    if (newValue?.customerType === 'wholesale' && !canWholesale) {
                      return;
                    }
                    setFormData({ ...formData, customerId: newValue?.id || '' });
                    if (newValue?.customerType === 'wholesale' && canWholesale) {
                      handlePricingModeChange('wholesale');
                    } else if (newValue?.customerType === 'retail' && pricingMode === 'wholesale') {
                      handlePricingModeChange('retail');
                    }
                  }}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const isRestrictedWholesale = !canWholesale && option.customerType === 'wholesale';
                    return (
                      <li key={key || option.id} {...otherProps} style={{ opacity: isRestrictedWholesale ? 0.5 : 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <span>{option.name}</span>
                          {option.customerType === 'wholesale' && (
                            <Chip
                              size="small"
                              label={canWholesale ? "Wholesale" : "Wholesale (Restricted)"}
                              color={canWholesale ? "secondary" : "default"}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, ml: 1 }}
                            />
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  onInputChange={(_, newInputValue) => {
                    if (newInputValue && newInputValue.length >= 2) {
                      triggerCustomerSearch({ search: newInputValue, limit: 20 });
                    } else if (!newInputValue) {
                      triggerCustomerSearch({ limit: 20 });
                    }
                  }}
                  onOpen={() => {
                    if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                  }}
                  renderInput={(params) => <TextField {...params} label="Customer Name *" />}
                />
                <Button
                  variant="outlined"
                  onClick={() => setCustomerDialogOpen(true)}
                  sx={{ height: 56, minWidth: 44 }}
                  title="Add Customer"
                >
                  <PersonAddAltIcon fontSize="small" />
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Customer TRN (if VAT registered)"
                value={selectedCustomer?.trn ?? ''}
                InputProps={{ readOnly: true }}
                placeholder="From customer"
              />
            </Grid>

            {/* Hold Detail Headers */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Hold Number"
                value={hold?.holdNumber || '–'}
                disabled
                helperText="Generated on save"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date *"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Sales Type"
                value={formData.salesType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    salesType: e.target.value,
                    creditDays: e.target.value === 'credit' ? prev.creditDays : '',
                  }))
                }
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>

            {formData.salesType === 'credit' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Credit days"
                  value={formData.creditDays}
                  onChange={(e) => setFormData({ ...formData, creditDays: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {creditDaysOptions.map((d) => (
                    <MenuItem key={d} value={String(d)}>
                      {d} days
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Sales Person"
                value={formData.salesPersonId}
                onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Branch / Store"
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {(locations || []).map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Line Items Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Line Items
                  </Typography>
                  {canWholesale && (
                    <ToggleButtonGroup
                      value={pricingMode}
                      exclusive
                      onChange={(e, newMode) => {
                        if (newMode) handlePricingModeChange(newMode);
                      }}
                      size="small"
                      sx={{
                        height: 36,
                        '& .MuiToggleButton-root': {
                          px: 1.5,
                          py: 0.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '0.82rem',
                        },
                        '& .Mui-selected': {
                          bgcolor: pricingMode === 'wholesale' ? '#6C5CE7 !important' : '#1976D2 !important',
                          color: '#fff !important',
                        },
                      }}
                    >
                      <ToggleButton value="retail">
                        🏷️ Retail Price
                      </ToggleButton>
                      <ToggleButton value="wholesale">
                        🏢 Wholesale Price
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    minWidth: { xs: '100%', sm: 420 },
                    maxWidth: 700,
                  }}
                >
                  {/* Quick Price Override Input Box */}
                  {company?.enableQuickScanPrice !== false && (
                    <Tooltip title="Type custom price, then scan barcode to apply automatically" arrow>
                      <TextField
                        size="medium"
                        type="number"
                        id="quick-scan-price-input"
                        inputRef={quickPriceInputRef}
                        label={`Price (${currency})`}
                        placeholder="0.00"
                        value={quickScanPrice}
                        onChange={(e) => setQuickScanPrice(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.8rem' }}>
                                {currency}
                              </Typography>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 0.25 }}>
                              {quickScanPrice !== '' && (
                                <IconButton size="small" onClick={() => setQuickScanPrice('')} sx={{ p: 0.25 }} title="Clear Price">
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                              <Tooltip title={lockQuickScanPrice ? 'Price Locked (applies to all scans)' : 'Price Unlocked (clears after single scan)'} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => setLockQuickScanPrice(!lockQuickScanPrice)}
                                  color={lockQuickScanPrice ? 'primary' : 'default'}
                                  sx={{ p: 0.25 }}
                                >
                                  {lockQuickScanPrice ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                        sx={{
                          width: { xs: 130, sm: 160 },
                          flexShrink: 0,
                          '& .MuiInputBase-root': {
                            height: 52,
                            borderRadius: 2,
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            bgcolor: quickScanPrice ? '#f0fdf4' : 'background.paper',
                            '& fieldset': {
                              borderColor: quickScanPrice ? '#22c55e' : undefined,
                              borderWidth: quickScanPrice ? '2px' : undefined,
                            },
                          },
                        }}
                      />
                    </Tooltip>
                  )}

                  <Autocomplete
                    fullWidth
                    size="medium"
                    autoHighlight
                    freeSolo
                    options={searchingProducts ? [] : (productSearchData?.products || [])}
                    loading={searchingProducts}
                    filterOptions={(x) => x}
                    value={null}
                    inputValue={productSearch}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      if (typeof option === 'string') return option;
                      const label = formatItemDisplayName(
                        { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                        itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                      );
                      return label || '';
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      const isWholesale = pricingMode === 'wholesale';
                      return (
                        <li key={key || option.id} {...otherProps}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatItemDisplayName(
                                  { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                                  itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Stock: {option.stock ?? option.quantity ?? 0} {option.unit || 'pcs'}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: isWholesale ? 400 : 700,
                                  color: isWholesale ? 'text.secondary' : 'primary.main',
                                }}
                              >
                                RT: {currency} {Number(option.sellingPrice || 0).toFixed(company?.currencyDecimals ?? 2)}
                              </Typography>
                              {canWholesale && Number(option.wholesalePrice) > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: isWholesale ? 700 : 400,
                                    color: isWholesale ? 'secondary.main' : 'text.secondary',
                                  }}
                                >
                                  WS: {currency} {Number(option.wholesalePrice || 0).toFixed(company?.currencyDecimals ?? 2)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </li>
                      );
                    }}
                    onInputChange={(_, value) => {
                      setProductSearch(value);
                      if (value && value.trim().length >= 1) {
                        triggerProductSearch({ search: value.trim(), limit: 20 });
                      }
                    }}
                    onChange={(_, value) => {
                      if (value && typeof value === 'object') {
                        handleAddProductRow(value);
                        setProductSearch('');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="product-search-input"
                        label="Scan or search item (name / barcode)"
                        placeholder="Type or paste name / barcode"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const list = searchingProducts ? [] : (productSearchData?.products || []);
                            if (list.length > 0) {
                              const typed = String(productSearch).trim();
                              const firstItem = list[0];
                              if (typed) {
                                const isExactMatch =
                                  String(firstItem.barcode || '').trim() === typed ||
                                  String(firstItem.sku || '').trim() === typed ||
                                  firstItem.name.toLowerCase().includes(typed.toLowerCase());
                                if (!isExactMatch) return;
                              }
                              handleAddProductRow(firstItem);
                              setProductSearch('');
                            }
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 52,
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                  />
                  <IconButton
                    title="Scan barcode"
                    sx={{
                      height: 52,
                      width: 52,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <QrCodeScannerIcon />
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, width: 56 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Product Name *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Batch No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Quantity *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Selling Price *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                      {isTaxEnabled && <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>}
                      {isTaxEnabled && <TableCell sx={{ fontWeight: 600 }}>VAT Amount</TableCell>}
                      <TableCell sx={{ fontWeight: 600 }}>Line Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const { vatAmount, lineTotal } = computeLine(item);
                      return (
                        <TableRow key={index}>
                          <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {formatItemDisplayName(
                                { name: item.productName, sku: item.productSku, brand: item.productBrand },
                                itemNameFormat
                              )}
                            </Typography>
                            {company?.enableServiceModule && item.productType === 'service' && (
                              <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#fbf8ff', border: '1px solid #e1d5e7', maxWidth: 400 }}>
                                <Typography variant="caption" color="secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                  🔧 Service / Repair Details
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      size="small"
                                      label="Device Model"
                                      placeholder="e.g. iPhone 14 Pro"
                                      value={item.serviceData?.deviceModel || ''}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          deviceModel: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      size="small"
                                      label="IMEI / Serial"
                                      placeholder="IMEI or Serial No"
                                      value={item.serviceData?.imei || ''}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          imei: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      size="small"
                                      label="Fault / Complains"
                                      placeholder="Describe issues / diagnostic notes"
                                      value={item.serviceData?.complains || ''}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          complains: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                      multiline
                                      rows={1}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {item.productBarcode ?? '–'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.batchNo ?? ''}
                              onChange={(e) => handleItemChange(index, 'batchNo', e.target.value)}
                              placeholder="If applicable"
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              value={item.expiryDate ?? ''}
                              onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 130 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current - 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                title="Decrease"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                id={`qty-input-${index}`}
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                required
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current + 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                title="Increase"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.unit ?? 'pcs'}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField
                                id={`price-input-${index}`}
                                type="number"
                                size="small"
                                value={item.unitPrice}
                                onChange={(e) => {
                                  handleItemChange(index, 'unitPrice', e.target.value);
                                  handleItemChange(index, 'inclusivePriceInput', undefined);
                                }}
                                sx={{ width: 90 }}
                                inputProps={{ min: 0, step: 0.01 }}
                                required
                              />
                              {!isView && canWholesale && Number(item.wholesalePrice) > 0 && (
                                <Tooltip
                                  title={
                                    item.pricingType === 'wholesale'
                                      ? `Wholesale Active. Click to switch to Retail (${currency} ${Number(item.sellingPrice || 0).toFixed(company?.currencyDecimals ?? 2)})`
                                      : `Retail Active. Click to switch to Wholesale (${currency} ${Number(item.wholesalePrice || 0).toFixed(company?.currencyDecimals ?? 2)})`
                                  }
                                >
                                  <Chip
                                    size="small"
                                    label={item.pricingType === 'wholesale' ? 'WS' : 'RT'}
                                    color={item.pricingType === 'wholesale' ? 'secondary' : 'default'}
                                    variant={item.pricingType === 'wholesale' ? 'filled' : 'outlined'}
                                    onClick={() => {
                                      const nextType = item.pricingType === 'wholesale' ? 'retail' : 'wholesale';
                                      const targetBase =
                                        nextType === 'wholesale'
                                          ? Number(item.wholesalePrice)
                                          : Number(item.sellingPrice || 0);
                                      const itemVat = toNum(item.vatPercent);
                                      const calculatedUnitPrice =
                                        vatIncluded && itemVat > 0
                                          ? Number((targetBase / (1 + itemVat / 100)).toFixed(company?.currencyDecimals ?? 2))
                                          : targetBase;

                                      setItems((prev) => {
                                        const updated = [...prev];
                                        updated[index] = {
                                          ...updated[index],
                                          unitPrice: calculatedUnitPrice,
                                          inclusivePriceInput: undefined,
                                          pricingType: nextType,
                                        };
                                        return updated;
                                      });
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      fontWeight: 700,
                                      fontSize: '0.68rem',
                                      height: 24,
                                      px: 0.5,
                                      userSelect: 'none',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={
                                item.inclusivePriceInput !== undefined
                                  ? item.inclusivePriceInput
                                  : item.unitPrice === ''
                                  ? ''
                                  : Number(
                                      (toNum(item.unitPrice) * (1 + toNum(item.vatPercent) / 100)).toFixed(
                                        typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2
                                      )
                                    ).toString()
                              }
                              onChange={(e) => {
                                const nextInclusive = e.target.value;
                                handleItemChange(index, 'inclusivePriceInput', nextInclusive);
                                if (nextInclusive === '') {
                                  handleItemChange(index, 'unitPrice', '');
                                } else {
                                  const exclusivePrice =
                                    Number(nextInclusive) / (1 + toNum(item.vatPercent) / 100);
                                  handleItemChange(
                                    index,
                                    'unitPrice',
                                    exclusivePrice.toFixed(
                                      typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2
                                    )
                                  );
                                }
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount}
                              onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0 }}
                            />
                          </TableCell>
                          {isTaxEnabled && (
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                value={item.vatPercent}
                                onChange={(e) => handleItemChange(index, 'vatPercent', e.target.value)}
                                sx={{ width: 60 }}
                                inputProps={{ min: 0, max: 100 }}
                              />
                            </TableCell>
                          )}
                          {isTaxEnabled && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {vatAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 500 }}>
                            {lineTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Hold Footer Summary */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Bill Discount"
                value={billDiscount}
                onChange={(e) => setBillDiscount(Number(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Grand Total: {currency} {grandTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Round Off"
                  value={formData.roundOff}
                  onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                  inputProps={{ step: 0.01 }}
                  sx={{ width: 120 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1.4rem' }}>
                  Total: {currency} {totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                </Typography>
              </Box>

              {/* Payment Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
                Payment
              </Typography>
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Account (optional)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Amount (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.method}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].method = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="bank">Bank</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="cheque">Cheque</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.paymentAccountId || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].paymentAccountId = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 180 }}
                          >
                            <MenuItem value="">Auto (by method)</MenuItem>
                            {paymentAccounts?.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={p.referenceNo || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].referenceNo = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={p.amount}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].amount = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ width: 120 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setPayments(payments.filter((_, i) => i !== index));
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const currentPaid = payments.reduce((sum, p) => sum + toNum(p.amount), 0);
                            const bal = Math.max(0, totalAmount - currentPaid);
                            setPayments((prev) => [
                              ...prev,
                              {
                                method: 'cash',
                                paymentAccountId: '',
                                referenceNo: '',
                                amount: bal.toFixed(company?.currencyDecimals ?? 2),
                              },
                            ]);
                            setAutoPaymentAmount(false);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Add Payment Method
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Paid Total: {currency} {paidTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: remaining > 0 ? 'error.main' : 'success.main' }}>
                  {remaining >= 0 ? `Remaining: ${currency} ${remaining.toFixed(company?.currencyDecimals ?? 2)}` : `Change: ${currency} ${Math.abs(change).toFixed(company?.currencyDecimals ?? 2)}`}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button type="submit" variant="contained" size="large" sx={{ px: 4, borderRadius: 2 }}>
                  {isNew ? 'Create Hold' : 'Save Hold'}
                </Button>
                <Button type="button" variant="outlined" size="large" onClick={() => navigate('/sales/holds')} sx={{ borderRadius: 2 }}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Customer</DialogTitle>
        <DialogContent>
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) setFormData((prev) => ({ ...prev, customerId: created.id }));
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete this hold?</DialogTitle>
        <DialogContent>
          <Typography>This cannot be undone. The hold will be removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HoldForm;
